import { memo } from 'react';
import './styles/TeacherNoteListItem.scss';

import { ListItemButton, Stack, Tooltip, Typography } from '@mui/material';
import { AttachFile, ChatBubbleOutline, DoneAll, Mic } from '@mui/icons-material';

import { formatVoiceNoteTime, HStack } from '@dw/components/core';
import { classNames, componentClassName, formatCompactActivityTime, getColorCss } from '@dw/utils';
import { IUnifiedTeacherNote } from '@dw/models/teacherNote';

import { TeacherNoteDirectionLabel } from './TeacherNoteDirectionLabel';

const cn = componentClassName('teacher-note-list-item');

interface ITeacherNoteListItemProps {
  note: IUnifiedTeacherNote;
  isSelected: boolean;
  /** Takes the note so the caller can pass one stable handler for every row (keeping memo effective). */
  onSelect: (note: IUnifiedTeacherNote) => void;
}

/**
 * One compact row in the conversation rail: two lines, with the unread dot and relative time
 * held in a consistent right-hand meta column so a long list stays scannable.
 *
 * Memoised — with a stable select handler, clicking a row re-renders only the rows whose
 * selected state actually changed rather than the whole list.
 */
export const TeacherNoteListItem = memo(function TeacherNoteListItem(props: ITeacherNoteListItemProps) {

  const { note, isSelected, onSelect } = props;

  const isUnread = note.hasUnreadActivity;
  // Everything demanding attention on this row: unread replies, plus the note itself if never opened.
  const unreadCount = note.unreadReplyCount + (note.isNoteUnread ? 1 : 0);

  // Delivery/read ticks belong to notes you sent — a received note has no receipts to report.
  const recipients = note.recipients;
  const readCount = recipients?.filter(x => !!x.readTimestamp).length ?? 0;
  const allRead = !!recipients && recipients.length > 0 && readCount === recipients.length;

  const previewFontWeight = isUnread ? 500 : 400;

  // A voice-only note carries no text, so the preview line names what it IS instead of rendering
  // blank. Anything with text previews the text, voice note or not.
  const preview = !note.text && note.voiceNote
    ? (
      <HStack breakpoint={false} columnGap={0.5} flex={1}>
        <Mic fontSize='small' color='disabled' />
        <Typography variant='caption' noWrap fontWeight={previewFontWeight}>
          Voice note · {formatVoiceNoteTime(note.voiceNote.durationSeconds)}
        </Typography>
      </HStack>
    ) : (
      <Typography variant='caption' noWrap flex={1} fontWeight={previewFontWeight}>
        {note.text}
      </Typography>
    );

  return (
    <ListItemButton
      selected={isSelected}
      onClick={() => onSelect(note)}
      aria-current={isSelected ? 'true' : undefined}
      className={classNames(cn(), { [cn('unread')]: isUnread })}
    >
      <Stack rowGap={0.25} flex={1} className={cn('content')}>
        <HStack breakpoint={false} columnGap={1} justifyContent='space-between'>
          <HStack breakpoint={false} columnGap={1} flex={1} className={cn('title')}>
            <TeacherNoteDirectionLabel note={note} fontWeight={isUnread ? 700 : 500} />
          </HStack>

          <Typography variant='caption' noWrap flexShrink={0}>
            {formatCompactActivityTime(note.lastActivityTimestamp)}
          </Typography>
        </HStack>

        <HStack breakpoint={false} columnGap={1} justifyContent='space-between'>
          {preview}

          <HStack breakpoint={false} columnGap={0.5} flexShrink={0}>
            {note.attachments.length > 0 && (
              <HStack breakpoint={false} columnGap={0.25}>
                <AttachFile fontSize='small' color='disabled' />
                <Typography variant='caption'>{note.attachments.length}</Typography>
              </HStack>
            )}
            {note.replies.length > 0 && (
              <HStack breakpoint={false} columnGap={0.25}>
                <ChatBubbleOutline fontSize='small' color='disabled' />
                <Typography variant='caption'>{note.replies.length}</Typography>
              </HStack>
            )}
            {recipients && recipients.length > 0 && (
              <Tooltip title={`${readCount} of ${recipients.length} read`}>
                <DoneAll fontSize='small' color={allRead ? 'success' : 'disabled'} />
              </Tooltip>
            )}

            {/* Rightmost, mirroring where every chat app puts its unread count. */}
            {unreadCount > 0 && (
              <Typography
                variant='caption'
                fontWeight={700}
                className={cn('unread-count')}
                sx={{ color: getColorCss('light') }}
                aria-label={`${unreadCount} unread`}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Typography>
            )}
          </HStack>
        </HStack>
      </Stack>
    </ListItemButton>
  );
});
